import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  SxProps,
  Theme,
} from "@mui/material";
import { SectionContainer } from "../../../modules/SectionContainer/SectionContainer";
import { SectionTitle } from "../../../modules/SectionTitle/SectionTitle";
import { SectionSubtitle } from "../../../modules/SectionSubtitle/SectionSubtitle";

const internRequirements = [
  { label: "雇用形態", value: "長期インターン（6ヶ月以上）" },
  { label: "勤務地", value: "フルリモート（一部出社あり）" },
  { label: "給与", value: "時給1,300円〜（スキル見合い）" },
  { label: "使用技術", value: "Java / Python / React / AWS など（詳細は面接にて）" },
  { label: "求める人物像", value: "優秀なエンジニアを本気で目指している方。才能と努力を惜しまない方" },
  { label: "選考フロー", value: "書類選考 → 面接 → 内定" },
];

const newGradRequirements = [
  { label: "募集対象", value: "新卒 / 第二新卒・異業種転職　歓迎" },
  { label: "職種", value: "ソフトウェアエンジニア（バックエンド / フロントエンド / インフラ）" },
  { label: "雇用形態", value: "正社員" },
  { label: "勤務地", value: "フルリモート（一部出社あり）" },
  { label: "給与", value: "月給25万円〜（スキル見合い）" },
  { label: "使用技術", value: "Java / Python / React / AWS など（詳細は面接にて）" },
  { label: "求める人物像", value: "学歴不問。学ぶ意欲とコードを書いた経験を重視。異業種からの転職も歓迎" },
  { label: "福利厚生", value: "社会保険完備、書籍購入補助、勉強会参加支援、資格取得支援" },
  { label: "選考フロー", value: "書類選考 → 面接 → 内定" },
];

const midCareerRequirements = [
  { label: "職種", value: "ソフトウェアエンジニア（バックエンド / フロントエンド / インフラ / フルスタック）" },
  { label: "雇用形態", value: "正社員" },
  { label: "勤務地", value: "フルリモート（一部出社あり）" },
  { label: "給与", value: "経験・スキルに応じて応相談" },
  { label: "求める人物像", value: "実務経験1年以上（言語・FW不問）。大手企業案件に臆せず挑戦できる方" },
  { label: "福利厚生", value: "社会保険完備、書籍購入補助、勉強会参加支援、資格取得支援" },
  { label: "選考フロー", value: "書類選考 → 面接 → 内定" },
];

const categories = [
  { title: "新卒・第二新卒", requirements: newGradRequirements, entryUrl: "https://forms.gle/a5FuRxExi93rCKGt7" },
  { title: "中途", requirements: midCareerRequirements, entryUrl: "https://forms.gle/sLLutt1nN5r7AfQW6" },
  { title: "インターン", requirements: internRequirements, entryUrl: "https://forms.gle/fz5GBaGPgQJuwdF37" },
];

export const RequirementsSection = () => {
  const renderTable = (requirements: { label: string; value: string }[]) => (
    <TableContainer sx={tableContainerSx}>
      <Table>
        <TableBody>
          {requirements.map((req, index) => (
            <TableRow key={index} sx={index % 2 === 0 ? tableRowOddSx : tableRowEvenSx}>
              <TableCell sx={tableCellLabelSx}>{req.label}</TableCell>
              <TableCell sx={tableCellContentSx}>{req.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <SectionContainer sx={{ background: "#f8f9fa", padding: { md: "100px 0", xs: "60px 0" } }}>
      <Box id="requirements-section">
        <SectionTitle sx={{ fontSize: { md: "40px", xs: "26px" } }}>募集要項</SectionTitle>
        <SectionSubtitle sx={{ maxWidth: "700px" }}>
          テクノスタンダードでは、正社員・インターンと、さまざまな形で一緒に働ける仲間を募集しています。まずは気軽にご連絡ください。
        </SectionSubtitle>

        <Box sx={categoriesContainerSx}>
          {categories.map((category, index) => (
            <Box key={index} sx={categoryBoxSx}>
              <Typography sx={categoryTitleSx}>{category.title}</Typography>
              {renderTable(category.requirements)}
              <Box sx={ctaContainerSx}>
                <Button sx={ctaButtonSx} href={category.entryUrl} target="_blank">
                  エントリーする
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </SectionContainer>
  );
};

const categoriesContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { md: "48px", xs: "32px" },
  marginTop: { md: "48px", xs: "32px" },
};

const categoryBoxSx: SxProps<Theme> = {
  background: "#FFFFFF",
  borderRadius: "12px",
  padding: { md: "32px", xs: "20px" },
};

const categoryTitleSx: SxProps<Theme> = {
  fontSize: { md: "24px", xs: "20px" },
  fontWeight: 700,
  color: "#22A1B9",
  marginBottom: { md: "24px", xs: "16px" },
  paddingBottom: { md: "16px", xs: "12px" },
  borderBottom: "2px solid #22A1B9",
};

const tableContainerSx: SxProps<Theme> = {
  background: "#FFFFFF",
  borderRadius: "8px",
  border: "1px solid #E0E0E0",
};

const tableRowOddSx: SxProps<Theme> = {
  background: "#FFFFFF",
};

const tableRowEvenSx: SxProps<Theme> = {
  background: "#f8f9fa",
};

const tableCellLabelSx: SxProps<Theme> = {
  fontWeight: 700,
  color: "#222222",
  fontSize: { md: "14px", xs: "13px" },
  width: { md: "160px", xs: "100px" },
  borderBottom: "1px solid #E0E0E0",
  padding: { md: "16px", xs: "12px" },
  verticalAlign: "top",
};

const tableCellContentSx: SxProps<Theme> = {
  color: "#666666",
  fontSize: { md: "14px", xs: "13px" },
  lineHeight: 1.7,
  borderBottom: "1px solid #E0E0E0",
  padding: { md: "16px", xs: "12px" },
};

const ctaContainerSx: SxProps<Theme> = {
  marginTop: { md: "24px", xs: "20px" },
  textAlign: "center",
};

const ctaButtonSx: SxProps<Theme> = {
  background: "linear-gradient(135deg, #22A1B9, #00BFA5)",
  color: "#FFFFFF",
  fontWeight: 700,
  padding: { md: "14px 40px", xs: "12px 32px" },
  fontSize: { md: "15px", xs: "14px" },
  borderRadius: "8px",
  textTransform: "none",
  transition: "all 0.3s",
  boxShadow: "0 4px 15px rgba(34, 161, 185, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #1d8fa3, #00a890)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(34, 161, 185, 0.4)",
  },
};
